<template>
  <div class="form-group" :class="{ 'has-icon': !!icon }">
    <label :for="id" v-if="label">{{ label }}</label>
    <i v-if="icon" :class="'icon ' + icon"></i>
    <textarea
      v-if="textarea"
      v-model="innerValue"
      class="form-control"
      :class="classObject"
      :id="id"
      :placeholder="placeholder"
      :readonly="readonly"
      :disabled="disabled"
    />
    <div class="form-wrapper" v-else-if="type === 'password'">
      <input
        v-model="innerValue"
        class="form-control"
        :class="classObject"
        :id="id"
        :type="!showPassword && type"
        :placeholder="placeholder"
        :autocomplete="disableAutocomplete ? 'off' : ''"
        :autocorrect="disableAutocapitalize ? 'off' : ''"
        :autocapitalize="disableAutocapitalize ? 'none' : ''"
        :readonly="readonly"
        :disabled="disabled"
        @blur="blur"
      />
      <div
        :class="
          showError || showInnerError ? 'show-error-icon' : 'show-password-icon'
        "
      >
        <button
          v-if="!(showError || showInnerError)"
          type="button"
          class="clear-button pt-1"
          @click="showPassword = !showPassword"
        >
          <i
            :class="
              !(showPassword || showInnerError)
                ? 'fa-light fa-eye'
                : 'fa-light fa-eye-slash'
            "
          />
        </button>
      </div>
    </div>
    <input
      v-else
      v-model="innerValue"
      class="form-control"
      :class="classObject"
      :id="id"
      :type="type"
      :placeholder="placeholder"
      :autocomplete="disableAutocomplete ? 'off' : ''"
      :autocorrect="disableAutocapitalize ? 'off' : ''"
      :autocapitalize="disableAutocapitalize ? 'none' : ''"
      :readonly="readonly"
      :disabled="disabled"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
    />
    <button v-if="withClearButton && value.length" class="clear" @click="clear">
      <i class="icon-close"></i>
    </button>
    <div v-if="showError" class="invalid-feedback">{{ errors[0] }}</div>
  </div>
</template>

<script>
export default {
  props: {
    textarea: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    icon: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "text"
    },
    placeholder: {
      type: String,
      default: ""
    },
    id: {
      type: String,
      required: true
    },
    value: {
      type: String,
      default: ""
    },
    customClass: {
      type: String,
      default: ""
    },
    errors: {
      type: Array,
      default: () => []
    },
    disableAutocapitalize: {
      type: Boolean,
      default: false
    },
    disableAutocomplete: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    withClearButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      innerValue: this.value,
      showInnerError: false,
      showPassword: false,
      showError: false
    };
  },
  computed: {
    classObject() {
      const result = { "is-invalid": this.showError };
      result[this.customClass] = true;
      return result;
    }
  },
  watch: {
    errors(newVal) {
      if (newVal && newVal.length) {
        this.showError = true;
      }
    },
    innerValue(newVal) {
      if (newVal != this.value) {
        this.showError = false;
        this.$emit("input", newVal);
      }
    },
    value(newVal) {
      if (this.innerValue !== newVal) {
        this.innerValue = newVal;
      }
    }
  },
  methods: {
    async blur() {
      this.innerValue = this.innerValue + " ";
      this.innerValue = await this.innerValue.trim();

      if (!this.autoCheck) return;

      if (!this.innerValue.length) {
        this.$emit("setError", ["Este campo não pode ser em branco."], this.id);
        this.showInnerError = true;
        return;
      } else if (this.type == "email") {
        let regExp = /^[\w-\.]+@([\w-]+\.)+[\w-]/;
        if (!this.innerValue.match(regExp)) {
          this.$emit("setError", ["Insira um email válido."], this.id);
          this.showInnerError = true;
          return;
        }
      } else if (this.type == "password" && this.innerValue.length < 6) {
        this.$emit(
          "setError",
          ["Certifique-se de que este campo tenha mais de 6 caracteres."],
          this.id
        );
        this.showInnerError = true;
        return;
      }

      this.$emit("setError", [""], this.id);
      this.showInnerError = false;
    },
    clear() {
      this.$emit("input", "");
    }
  }
};
</script>

<style lang="scss" scoped>
.form-group {
  position: relative;
  font-size: 12px;

  .form-control {
    min-height: 48px;
    border-radius: 4px;
    padding: 8px 8px 8px 12px;
    font-size: 14px;
  }

  &.has-icon {
    .icon {
      position: absolute;
      line-height: 38px;
      left: 4px;
    }

    .form-control {
      padding-left: 32px;
    }
  }

  .clear {
    position: absolute;
    height: 38px;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    background: transparent;
    border: none;
  }

  textarea {
    border-radius: 10px;
    padding: 15px 18px 1px 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0.015em;
    height: 48px;
    resize: none;
    height: 100px;

    &::placeholder {
      opacity: 1; /* Firefox */
      font-size: 14px;
      font-weight: 400;
      line-height: 21px;
      letter-spacing: 0.015em;
      color: #d0d1ddd9;
    }
  }

  .form-wrapper {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;

    .form-control {
      padding-right: 7px;
    }
    .show-password-icon {
      position: absolute;
      right: 25px;
      top: 50%;
      font-size: 17px;
      cursor: pointer;
      user-select: none;
    }
    .show-error-icon {
      position: absolute;
      right: 25px;
      top: 52px;
      font-size: 17px;
      user-select: none;
      i {
        color: #ff3f34;
      }
    }
    .clear-button {
      background-color: transparent;
      padding: 0px;
      margin: 0px;
      border: 0px;
    }
  }
}
</style>
